<script lang="ts">
  import { t } from "../../helpers/translations";
  import { currencyFormatter } from "../../helpers/localise";
  import TextNew from "../TextNew.svelte";
  import DisplayCard from "./DisplayCard.svelte";

  const cardFlex = "0 1 auto";
  const cardMinH = "88px";

  export let readonly = false;
  export let isLoading: boolean;
  export let totalSpend: number | undefined = undefined;
  export let totalRevenue: number | null = null;
  export let grossProfit: number | null = null;
  export let gpTarget: number | null = null;
  export let isDefaultGp = true;
  export let isProfitable = false;
  export let onGpTargetChange: (gpTarget: number) => void = () => {};

  $: missingGpTarget = gpTarget === null;
  $: formattedTotalSpend = $currencyFormatter.format((totalSpend ?? 0) / 100);
  $: formattedTotalRevenue =
    typeof totalRevenue === "number" ? $currencyFormatter.format(totalRevenue / 100) : "—";
  $: grossProfitDisplay =
    typeof grossProfit === "number" ? (grossProfit * 100).toFixed(1) + "%" : "—";
  $: serverGPTargetPercentage = gpTarget ? (gpTarget / 100).toFixed(0) : 0;
  $: gpTargetPercentage = serverGPTargetPercentage;
  $: hasNonDefaultGpTarget = !isDefaultGp && !!gpTargetPercentage;
  $: gpTargetPercentageWidth = gpTargetPercentage
    ? `${(gpTargetPercentage ?? 70).toString().length}ch`
    : "2ch";

  let gpTargetInput: HTMLInputElement;

  function handleGpTargetChangeClick() {
    gpTargetInput.focus();
  }

  function handleGpTargetChange(event: Event) {
    const value = (event.target as HTMLInputElement).value;

    if ((event.target as HTMLInputElement).checkValidity() === false) {
      return;
    }
    const nextGpTarget = value ? parseInt(value, 10) * 100 : gpTarget;
    if (nextGpTarget === gpTarget || nextGpTarget === null) {
      return;
    }

    onGpTargetChange(nextGpTarget);
  }

  const grossSpendLabel = t("gpTracker.spend.label");
  const revenueLabel = t("gpTracker.revenue.label");
  const gpTargetLabel = t("gpTracker.target.label");
  const grossProfitLabel = t("gpTracker.grossProfit.label");
  const roundness = "s";
</script>

<div class="grid-2">
  {#if totalSpend !== undefined && !isLoading}
    <DisplayCard label={grossSpendLabel} --card-flex={cardFlex} {roundness}>
      <TextNew slot="display" size="l">{formattedTotalSpend}</TextNew>
    </DisplayCard>
    <DisplayCard label={revenueLabel} --card-flex={cardFlex} {roundness}>
      <div slot="display" class="input-container">
        <TextNew as="span" size="l" --text-display="block">{formattedTotalRevenue}</TextNew>
      </div>
    </DisplayCard>
    <DisplayCard
      label={gpTargetLabel}
      {roundness}
      --card-flex={cardFlex}
      on:cardclick={handleGpTargetChangeClick}
    >
      <div slot="display" class="input-container">
        <input
          class="calc-input"
          data-text-low={!hasNonDefaultGpTarget}
          type="number"
          {readonly}
          disabled={missingGpTarget}
          bind:value={gpTargetPercentage}
          bind:this={gpTargetInput}
          placeholder="70"
          min="0"
          max="100"
          on:change={handleGpTargetChange}
          style:width={gpTargetPercentageWidth}
        />
        <TextNew
          as="span"
          size="l"
          --text-display="block"
          --rk-text-color={!hasNonDefaultGpTarget ? "var(--rk-text-low)" : "var(--rk-text-color)"}
        >
          %
        </TextNew>
      </div>
    </DisplayCard>
    <DisplayCard label={grossProfitLabel} --card-flex={cardFlex} {roundness}>
      <TextNew
        slot="display"
        size="l"
        --rk-text-color={grossProfit === null
          ? "var(--rk-text-color)"
          : isProfitable
          ? "var(--rk-text-success)"
          : "var(--rk-text-error)"}>{grossProfitDisplay}</TextNew
      >
    </DisplayCard>
  {:else}
    <DisplayCard
      {roundness}
      label={grossSpendLabel}
      --card-flex={cardFlex}
      --card-minH={cardMinH}
    />
    <DisplayCard {roundness} label={revenueLabel} --card-flex={cardFlex} --card-minH={cardMinH} />
    <DisplayCard {roundness} label={gpTargetLabel} --card-flex={cardFlex} --card-minH={cardMinH} />
    <DisplayCard
      {roundness}
      label={grossProfitLabel}
      --card-flex={cardFlex}
      --card-minH={cardMinH}
    />
  {/if}
</div>

<style>
  input::-webkit-outer-spin-button,
  input::-webkit-inner-spin-button {
    -webkit-appearance: none;
    margin: 0;
  }

  input[type="number"] {
    appearance: auto;
    -moz-appearance: textfield; /* Firefox */
  }
  .grid-2 {
    display: grid;
    grid-template-columns: repeat(2, minmax(166px, 1fr));
    gap: 8px;
  }

  .input-container {
    display: flex;
    align-items: center;
  }

  .calc-input {
    font-family: "DiatypeRekki";
    font-weight: 400;
    font-size: var(--rk-font-size-l);
    line-height: var(--rk-line-height-l);
    letter-spacing: -1px;
    min-width: 0;
  }

  .calc-input::placeholder {
    color: var(--rk-text-low);
  }

  .calc-input:blur {
    color: var(--rk-text-low);
  }

  .calc-input:disabled {
    color: var(--rk-text-low);
  }

  .calc-input:focus {
    color: var(--rk-text-color);
  }

  .calc-input[data-text-low="true"] {
    color: var(--rk-text-low);
  }
</style>
